




































































































import Vue, { PropType } from 'vue';

import { BaseOrganization } from '@/models';
import EditableFieldWithDefault, {
  allEmptyOrNull,
  buildInitialModel,
  EditableFieldState,
  normalizeAgainstDefault
} from '@/components/EditableFieldWithDefault.vue';

export interface RoleNames {
  roleNameStudent: string | null;
  roleNameStudentPlural: string | null;
  roleNameTeacher: string | null;
  roleNameTeacherPlural: string | null;
}

export interface RoleNameUpdateEvent {
  dirty: boolean;
  outputModel: RoleNames;
}

/// Takes a set of derived names and org names and returns null if:
/// - The derived name matches the org name
/// - The derived name is an empty string or null
export const normalizeAgainstOrg = (
  roleNames: RoleNames,
  org: BaseOrganization
): RoleNames => {
  return {
    roleNameStudent: normalizeAgainstDefault(
      roleNames.roleNameStudent,
      org.roleNameStudent
    ),
    roleNameStudentPlural: normalizeAgainstDefault(
      roleNames.roleNameStudentPlural,
      org.roleNameStudentPlural
    ),
    roleNameTeacher: normalizeAgainstDefault(
      roleNames.roleNameTeacher,
      org.roleNameTeacher
    ),
    roleNameTeacherPlural: normalizeAgainstDefault(
      roleNames.roleNameTeacherPlural,
      org.roleNameTeacherPlural
    )
  };
};

const rolesAreDefault = (roleNames: RoleNames): boolean => {
  return allEmptyOrNull([
    roleNames.roleNameStudent,
    roleNames.roleNameStudentPlural,
    roleNames.roleNameTeacher,
    roleNames.roleNameTeacherPlural
  ]);
};

export default Vue.extend({
  components: {
    EditableFieldWithDefault
  },

  props: {
    // The roleNames prop value is the _derived_ values
    // for role names.  We call `normalizeRoleNames` to determine
    // which of the values are inherited from the org.
    roleNames: {
      type: Object as PropType<RoleNames>,
      required: true
    },
    organization: {
      type: Object as PropType<BaseOrganization>,
      required: true
    }
  },

  data() {
    const state = {
      roleNameStudentInputState: buildInitialModel(
        this.roleNames.roleNameStudent,
        this.organization.roleNameStudent
      ),
      roleNameStudentPluralInputState: buildInitialModel(
        this.roleNames.roleNameStudentPlural,
        this.organization.roleNameStudentPlural
      ),
      roleNameTeacherInputState: buildInitialModel(
        this.roleNames.roleNameTeacher,
        this.organization.roleNameTeacher
      ),
      roleNameTeacherPluralInputState: buildInitialModel(
        this.roleNames.roleNameTeacherPlural,
        this.organization.roleNameTeacherPlural
      )
    };

    // Initial value reflects the persisted state -- if roles are different
    // than the defaults, begin with the custom input open.
    const startOpen = !rolesAreDefault(this.roleNames);

    return {
      showCustomInput: startOpen,
      ...state
    } as {
      showCustomInput: boolean;
      roleNameStudentInputState: EditableFieldState;
      roleNameStudentPluralInputState: EditableFieldState;
      roleNameTeacherInputState: EditableFieldState;
      roleNameTeacherPluralInputState: EditableFieldState;
    };
  },

  computed: {
    inputStates(): EditableFieldState[] {
      return [
        this.roleNameStudentInputState,
        this.roleNameStudentPluralInputState,
        this.roleNameTeacherInputState,
        this.roleNameTeacherPluralInputState
      ];
    },

    unchanged(): boolean {
      return this.inputStates.every(state => state.unchanged);
    },

    outputModel(): RoleNames {
      const updated: RoleNames = {
        roleNameStudent: this.roleNameStudentInputState.outputValue,
        roleNameStudentPlural: this.roleNameStudentPluralInputState.outputValue,
        roleNameTeacher: this.roleNameTeacherInputState.outputValue,
        roleNameTeacherPlural: this.roleNameTeacherPluralInputState.outputValue
      };

      return updated;
    },

    emittedEvent(): RoleNameUpdateEvent {
      return {
        dirty: !this.unchanged,
        outputModel: this.outputModel
      };
    },

    derivedNames(): RoleNames {
      return {
        roleNameStudent: this.roleNameStudentInputState.apparentValue,
        roleNameStudentPlural: this.roleNameStudentPluralInputState
          .apparentValue,
        roleNameTeacher: this.roleNameTeacherInputState.apparentValue,
        roleNameTeacherPlural: this.roleNameTeacherPluralInputState
          .apparentValue
      };
    }
  },

  methods: {
    handleStudentUpdate(value: EditableFieldState) {
      this.roleNameStudentInputState = value;
    },
    handleStudentPluralUpdate(value: EditableFieldState) {
      this.roleNameStudentPluralInputState = value;
    },
    handleTeacherUpdate(value: EditableFieldState) {
      this.roleNameTeacherInputState = value;
    },
    handleTeacherPluralUpdate(value: EditableFieldState) {
      this.roleNameTeacherPluralInputState = value;
    },

    // If we're either enabling or disabling custom input, we
    // need to clear the model.
    clearModel() {
      this.roleNameStudentInputState.inputValue = null;
      this.roleNameStudentPluralInputState.inputValue = null;
      this.roleNameTeacherInputState.inputValue = null;
      this.roleNameTeacherPluralInputState.inputValue = null;
    }
  },

  watch: {
    emittedEvent: {
      deep: true,
      handler(value) {
        this.$emit('change', value);
      }
    }
  }
});
